import React, { useState, useEffect } from 'react';
import {
  Timeline,
  Space,
  Avatar,
  Typography,
  Empty,
  Spin,
  Tag,
  Button,
  Image,
  message,
  Modal,
} from 'antd';
import { DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import {
  PhoneOutlined,
  MessageOutlined,
  MailOutlined,
  TeamOutlined,
  RobotOutlined,
  PaperClipOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { getFollowUps, FollowUp } from '../../services/followUp';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');
dayjs.tz.setDefault('Asia/Shanghai'); // 设置默认时区为东八区

const { Text } = Typography;

interface FollowUpTimelineProps {
  leadId: number;
  refreshTrigger?: number; // Add refresh trigger
}

const FollowUpTimeline: React.FC<FollowUpTimelineProps> = ({ leadId, refreshTrigger }) => {
  const [loading, setLoading] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');

  // Load follow-ups
  const loadFollowUps = async (pageNum: number = 1, append: boolean = false) => {
    if (!leadId) return;
    
    setLoading(true);
    try {
      const response = await getFollowUps({
        entity_type: 'lead',
        entity_id: leadId,
        page: pageNum,
        pageSize: 20,
      });
      
      if (append) {
        setFollowUps(prev => [...prev, ...(response.items || [])]);
      } else {
        setFollowUps(response.items || []);
      }
      
      setTotal(response.total || 0);
      setHasMore((response.items || []).length === 20 && followUps.length + (response.items || []).length < response.total);
    } catch (error) {
      console.error('Failed to load follow-ups:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load more follow-ups
  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadFollowUps(nextPage, true);
  };

  useEffect(() => {
    if (leadId) {
      setPage(1);
      loadFollowUps(1, false);
    }
  }, [leadId, refreshTrigger]); // Add refreshTrigger to dependencies

  // Get follow-up type icon
  const getFollowUpIcon = (type?: string) => {
    if (!type) return <MessageOutlined />;
    const iconMap: Record<string, React.ReactNode> = {
      phone: <PhoneOutlined style={{ color: '#1890ff' }} />,
      message: <MessageOutlined style={{ color: '#52c41a' }} />,
      email: <MailOutlined style={{ color: '#faad14' }} />,
      meeting: <TeamOutlined style={{ color: '#722ed1' }} />,
      system: <RobotOutlined style={{ color: '#8c8c8c' }} />, // 系统操作
    };
    return iconMap[type] || <MessageOutlined />;
  };

  // Get follow-up type text
  const getFollowUpTypeText = (type?: string) => {
    if (!type) return '其他';
    const textMap: Record<string, string> = {
      phone: '电话',
      message: '短信',
      email: '邮件',
      meeting: '会议',
      visit: '拜访',
      system: '系统',
      other: '其他',
    };
    return textMap[type] || type;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin />
      </div>
    );
  }

  if (followUps.length === 0) {
    return (
      <div className="follow-up-timeline">
        <div className="section-title">跟进记录</div>
        <Empty
          description="暂无跟进记录"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="follow-up-timeline">
      <div className="section-title">跟进记录 ({total})</div>
      <Timeline>
        {followUps.map((followUp) => {
          const followType = followUp.follow_up_type || followUp.follow_type;
          const isSystem = followType === 'system';
          
          return (
            <Timeline.Item
              key={followUp.id}
              dot={
                <Avatar
                  size="small"
                  icon={getFollowUpIcon(followType)}
                  style={{
                    backgroundColor: isSystem ? '#f5f5f5' : '#e6f7ff'
                  }}
                />
              }
              color={isSystem ? 'gray' : 'blue'}
            >
              <div className={`timeline-item-content ${isSystem ? 'timeline-item-system' : ''}`}>
                <Space direction="vertical" size={4} style={{ width: '100%', maxWidth: '100%' }}>
                  <Space size="small" wrap style={{ maxWidth: '100%' }}>
                    <Text strong>
                      {followUp.created_by_name || followUp.creator_name || '未知用户'}
                    </Text>
                    {isSystem && (
                      <Tag color="default" style={{ margin: 0 }}>
                        系统操作
                      </Tag>
                    )}
                  </Space>
                  <div style={{ width: '100%', maxWidth: '100%' }}>
                    <Text style={{ 
                      wordBreak: 'break-word', 
                      whiteSpace: 'pre-wrap',
                      display: 'block'
                    }}>
                      {followUp.content}
                    </Text>
                  </div>
                  
                  {/* 显示附件 */}
                  {followUp.attachments && Array.isArray(followUp.attachments) && followUp.attachments.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Space wrap size="small">
                        {followUp.attachments.map((attachment: any, index: number) => {
                          // 兼容不同的字段名
                          const fileName = attachment.file_name || attachment.filename || attachment.name || '未知文件';
                          const filePath = attachment.file_path || attachment.url || attachment.cos_url || '';
                          
                          if (!fileName || !filePath) {
                            console.warn('Invalid attachment:', attachment);
                            return null;
                          }
                          
                          const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
                          const isPdf = /\.pdf$/i.test(fileName);
                          const isWord = /\.(doc|docx)$/i.test(fileName);
                          const isExcel = /\.(xls|xlsx)$/i.test(fileName);
                          
                          let icon = <FileOutlined />;
                          if (isImage) icon = <FileImageOutlined style={{ color: '#52c41a' }} />;
                          else if (isPdf) icon = <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
                          else if (isWord) icon = <FileWordOutlined style={{ color: '#1890ff' }} />;
                          else if (isExcel) icon = <FileExcelOutlined style={{ color: '#52c41a' }} />;
                          
                          return (
                            <Button
                              key={index}
                              size="small"
                              icon={icon}
                              onClick={async () => {
                                if (!attachment.id) {
                                  message.error('附件ID不可用');
                                  return;
                                }
                                try {
                                  // 调用后端API获取下载URL
                                  const response = await fetch(
                                    `/api/v1/attachments/${attachment.id}/download`,
                                    {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('law_firm_crm_token')}`,
                                      },
                                    }
                                  );
                                  if (!response.ok) {
                                    throw new Error('获取下载链接失败');
                                  }
                                  const data = await response.json();
                                  if (data.download_url) {
                                    // 打开预览模态框
                                    setPreviewUrl(data.download_url);
                                    setPreviewFileName(fileName);
                                    setPreviewVisible(true);
                                  } else {
                                    message.error('下载链接不可用');
                                  }
                                } catch (error) {
                                  console.error('Preview error:', error);
                                  message.error('获取预览链接失败');
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                maxWidth: 200,
                              }}
                            >
                              <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {fileName}
                              </span>
                            </Button>
                          );
                        })}
                      </Space>
                    </div>
                  )}
                  
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs.utc(followUp.created_at).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm')}
                    {' · '}
                    {dayjs.utc(followUp.created_at).tz('Asia/Shanghai').fromNow()}
                  </Text>
                </Space>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
      
      {/* 加载更多按钮 */}
      {hasMore && followUps.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button 
            type="link" 
            onClick={loadMore} 
            loading={loading}
            size="small"
          >
            加载更多 ({followUps.length} / {total})
          </Button>
        </div>
      )}

      {/* 文件预览模态框 */}
      {previewVisible && (
        <FilePreviewModal
          visible={previewVisible}
          fileUrl={previewUrl}
          fileName={previewFileName}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewUrl('');
            setPreviewFileName('');
          }}
        />
      )}
    </div>
  );
};

// 文件预览模态框组件
interface FilePreviewModalProps {
  visible: boolean;
  fileUrl: string;
  fileName: string;
  onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  visible,
  fileUrl,
  fileName,
  onClose,
}) => {
  // 判断文件类型
  const getFileExtension = (name: string): string => {
    const parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const extension = getFileExtension(fileName);
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
  const isPdf = extension === 'pdf';
  const isPreviewable = isImage || isPdf;

  // 渲染预览内容
  const renderPreviewContent = () => {
    if (isImage) {
      return (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Image
            src={fileUrl}
            alt={fileName}
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
            preview={false}
          />
        </div>
      );
    }

    if (isPdf) {
      return (
        <div style={{ height: '70vh', width: '100%' }}>
          <iframe
            src={fileUrl}
            title={fileName}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </div>
      );
    }

    // 不支持预览
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#999',
        }}
      >
        <Text type="secondary" style={{ fontSize: 16 }}>
          此文件类型不支持预览
        </Text>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            文件名：{fileName}
          </Text>
        </div>
        <div style={{ marginTop: 24 }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => window.open(fileUrl, '_blank')}
          >
            下载文件
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <span>文件预览</span>
          <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>
            {fileName}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={isPreviewable ? '80%' : 600}
      style={{ top: 20 }}
      footer={
        <Space>
          {isPreviewable && (
            <Button 
              icon={<DownloadOutlined />} 
              onClick={() => window.open(fileUrl, '_blank')}
            >
              下载
            </Button>
          )}
          <Button onClick={onClose} icon={<CloseOutlined />}>
            关闭
          </Button>
        </Space>
      }
      destroyOnClose
    >
      {renderPreviewContent()}
    </Modal>
  );
};

export default FollowUpTimeline;
